import { useMemo } from "react";
import { Marker, Polyline } from "react-leaflet";
import * as L from "leaflet";

type LatLngTuple = [number, number];

const WAYPOINT_PATH_COLOR = "#e11d48";

const basePathOptions: L.PolylineOptions = {
    color: WAYPOINT_PATH_COLOR,
    weight: 6,
    opacity: 0.95,
    lineCap: "round",
    lineJoin: "round",
};

const compactPathOptions: L.PolylineOptions = {
    ...basePathOptions,
    weight: 4,
    opacity: 0.95,
};

interface SegmentArrow {
    position: LatLngTuple;
    rotationDeg: number;
}

/**
 * Bearing from point A to B in screen space.
 * MapView uses CUSTOM_CRS with transformation (-1, 0, 1, 0), so map +x (lng)
 * is flipped on screen; atan2(dy, dx) alone does not match visible direction.
 */
const segmentBearingDeg = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const dLat = lat2 - lat1;
    const dLng = lng2 - lng1;
    return (Math.atan2(dLat, -dLng) * 180) / Math.PI;
};

const segmentArrows = (positions: LatLngTuple[]): SegmentArrow[] => {
    const arrows: SegmentArrow[] = [];
    for (let i = 0; i < positions.length - 1; i++) {
        const [lat1, lng1] = positions[i];
        const [lat2, lng2] = positions[i + 1];
        const dLat = lat2 - lat1;
        const dLng = lng2 - lng1;
        const length = Math.hypot(dLat, dLng);
        if (length < 1e-6) continue;

        arrows.push({
            position: [(lat1 + lat2) / 2, (lng1 + lng2) / 2],
            rotationDeg: segmentBearingDeg(lat1, lng1, lat2, lng2),
        });
    }
    return arrows;
};

const makeArrowIcon = (rotationDeg: number, size: number): L.DivIcon => {
    const half = size / 2;
    return new L.DivIcon({
        html:
            `<div style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;` +
            `transform:rotate(${rotationDeg}deg);pointer-events:none">` +
            `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">` +
            `<path d="M4 12H18M14 8L18 12L14 16" stroke="${WAYPOINT_PATH_COLOR}" stroke-width="3.5" ` +
            `stroke-linecap="round" stroke-linejoin="round"/></svg></div>`,
        className: "",
        iconSize: [size, size],
        iconAnchor: [half, half],
    });
};

interface WaypointDirectionPathProps {
    positions: LatLngTuple[];
    compact?: boolean;
}

const WaypointDirectionPath = ({ positions, compact = false }: WaypointDirectionPathProps) => {
    const arrowSize = compact ? 26 : 36;
    const pathOptions = compact ? compactPathOptions : basePathOptions;

    const arrows = useMemo(() => segmentArrows(positions), [positions]);
    const arrowIcons = useMemo(
        () => arrows.map((arrow) => makeArrowIcon(arrow.rotationDeg, arrowSize)),
        [arrows, arrowSize]
    );

    if (positions.length < 2) {
        return null;
    }

    return (
        <>
            <Polyline
                key="waypoint-polyline"
                interactive={false}
                positions={positions}
                pathOptions={pathOptions}
            />
            {arrows.map((arrow, index) => (
                <Marker
                    key={`waypoint-arrow-${index}`}
                    position={arrow.position}
                    icon={arrowIcons[index]}
                    interactive={false}
                    bubblingMouseEvents={false}
                />
            ))}
        </>
    );
};

export default WaypointDirectionPath;
